import * as React from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import { aqIdExistsAPI } from 'api/aqId'
import { Binary, BinaryType, Cancellation, PaymentStatus, PositionNumber, PositionNumberType, castConstantType } from 'api/common/constants'
import { AtemQuestIdExistsRequest } from 'api/common/requests'
import { Status } from 'api/common/statuses'
import { AtemQuestId, PositionSimplified } from 'api/common/types'
import { positionContractAPI, positionsAPI } from 'api/position'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputField from 'components/InputField'
import loginData from 'utilities/loginData'
import { ValidateResult, atemQuestIdValidator, emailValidator } from 'utilities/validators'
import { PositionInfo } from 'utilities/resources/formItemValues'

type StateValue = {
  fromPositionManagement?: boolean
}

const SceneComponent: React.FC = () => {
  type FormData = {
    positionNumber: PositionNumberType
    atemQuestId: string
    positionId: number
    email: string
    invalid: boolean
    binaryPlacement: BinaryType | null
    /** 復活対象ポジションか（事前登録ユーザーの未入金+解約サブポジション） */
    isRevivalTarget: boolean
  }
  type FormMessages = {
    atemQuestId: string[]
    email: string[]
    binaryPlacement: string[]
  }

  const navigate = useNavigate()
  const location = useLocation()
  const stateValue = React.useMemo(() => (location.state ?? {}) as StateValue, [location])
  const { fromPositionManagement } = stateValue
  const [formData, setFormData] = React.useState<FormData[]>([])
  const [formMessages, setFormMessages] = React.useState<FormMessages[]>([])
  const [errorMessage, setErrorMessage] = React.useState<string>('')
  const [isSpinningSubmit, setIsSpinningSubmit] = React.useState(false)
  const [subPositionCount, setSubPositionCount] = React.useState(0)
  const [inputCount, setInputCount] = React.useState(0)
  const prevScene = fromPositionManagement ? '/positionManagement' : '/positionSelect'

  const handleAtemQuestId = (index: number, value: string) => {
    let _inputCount = inputCount
    if (formData[index].positionNumber !== PositionNumber.MainPosition) {
      if (!!formData[index].atemQuestId && !value && !formData[index].email) {
        // 元が入力されていたかつ、空文字が指定されたかつ、メールアドレスが入力されていなければカウントを減らす
        _inputCount--
      }
      if (!formData[index].atemQuestId && !!value && !formData[index].email) {
        // 元が空だったかつ、文字が入力されたかつ、メールアドレスも空だった場合はカウントを増やす
        _inputCount++
      }
    }
    formData[index].atemQuestId = value
    updateInputForm(_inputCount, formData)
  }
  const handleEmail = (index: number, value: string) => {
    let _inputCount = inputCount
    if (!!formData[index].email && !value && !formData[index].atemQuestId) {
      // 元が入力されていたかつ、空文字が指定されたかつ、ATEMクエストIDが入力されていなければカウントを減らす
      _inputCount--
    }
    if (!formData[index].email && !!value && !formData[index].atemQuestId) {
      // 元が空だったかつ、文字が入力されたかつ、ATEMクエストIDも空だった場合はカウントを増やす
      _inputCount++
    }
    formData[index].email = value
    updateInputForm(_inputCount, formData)
  }
  /**
   * バイナリ配置選択の変更ハンドラ
   * サブポジションの左右配置（1:左, 2:右）を設定する
   * @param index フォームデータの配列インデックス
   * @param value 配置選択値（Binary.Left または Binary.Right）
   */
  const handleBinaryPlacement = (index: number, value: BinaryType) => {
    const newFormData = [...formData]
    newFormData[index].binaryPlacement = value
    setFormData(newFormData)
  }
  const updateInputForm = (count: number, data: FormData[]) => {
    data.forEach((d) => {
      if (d.positionNumber !== PositionNumber.MainPosition) {
        if (!d.atemQuestId && !d.email) {
          d.invalid = count >= subPositionCount
        }
      }
    })
    setFormData([...data])
    setInputCount(count)
    console.log(count)
  }
  const validation = async (): Promise<boolean> => {
    let isValidAll = true
    const newFormMessages: FormMessages[] = []
    let filledCount = 0 // 入力されたポジション数をカウント

    for (let i = 0; i < formData.length; i++) {
      const { atemQuestId, email, invalid, positionNumber, binaryPlacement, isRevivalTarget } = formData[i]
      const message: FormMessages = {
        atemQuestId: [],
        email: [],
        binaryPlacement: [],
      }
      newFormMessages.push(message)

      if (invalid) continue

      // メインポジション以外で、両方空の場合はスキップ（入力しなかったポジション）
      const isEmpty = !atemQuestId && !email
      if (positionNumber !== PositionNumber.MainPosition && isEmpty) {
        continue
      }

      // 入力されたポジションをカウント
      if (positionNumber !== PositionNumber.MainPosition) {
        filledCount++
      }

      const emailValidateResult = emailValidator(email)
      switch (emailValidateResult) {
        case ValidateResult.Empty:
          message.email.push('メールアドレスが空白です')
          isValidAll = false
          break
        case ValidateResult.Invalid:
          message.email.push('メールアドレスの形式が正しくありません')
          isValidAll = false
          break
      }

      const atemQuestIdValidateResult = atemQuestIdValidator(atemQuestId)
      switch (atemQuestIdValidateResult) {
        case ValidateResult.Empty:
          message.atemQuestId.push('AUBEクエストIDが空白です')
          isValidAll = false
          break
      }

      // サブポジションの場合は配置選択が必須（復活対象は既存バイナリマップを使用するため不要）
      if (positionNumber !== PositionNumber.MainPosition && !isRevivalTarget && binaryPlacement === null) {
        message.binaryPlacement.push('配置を選択してください')
        isValidAll = false
      }
    }

    // 入力されたサブポジション数が選択した数に足りているかチェック
    if (filledCount < subPositionCount) {
      setErrorMessage(`サブポジションを${subPositionCount}個以上入力してください`)
      setFormMessages(newFormMessages)
      return false
    }

    if (isValidAll) {
      // ATEMクエストID存在チェック（入力されたポジションのみ）
      const filledFormData: { index: number; data: FormData }[] = []
      formData.forEach((data, index) => {
        const isEmpty = !data.atemQuestId && !data.email
        if (!data.invalid && !isEmpty) {
          filledFormData.push({ index, data })
        }
      })

      const requestParam: AtemQuestIdExistsRequest = {
        atemQuestIds: filledFormData.map<AtemQuestId>(({ data }) => ({ atemQuestId: data.atemQuestId, email: data.email })),
      }
      await aqIdExistsAPI(requestParam, (response) => {
        response.exists.forEach((exists, i) => {
          if (!exists) {
            const originalIndex = filledFormData[i].index
            newFormMessages[originalIndex].atemQuestId.push('AUBEクエストIDが存在しません')
            isValidAll = false
          }
        })
      })
    }

    setFormMessages(newFormMessages)
    setErrorMessage('')
    return isValidAll
  }

  /**
   * 「お申込みへ」ボタン
   */
  const onSubmit = async (): Promise<void> => {
    setIsSpinningSubmit(true)
    if (await validation()) {
      // 入力されたポジションのみを契約対象とする（空のポジションは除外）
      const contractPositions: PositionSimplified[] = formData
        .filter((data) => !data.invalid && (data.atemQuestId || data.email))
        .map<PositionSimplified>((formData) => ({
          positionNumber: formData.positionNumber,
          atemQuestId: formData.atemQuestId,
          email: formData.email,
          binaryPlacement: formData.binaryPlacement,
        }))
      await positionContractAPI({ positions: contractPositions }, (status, result, positions) => {
        if (status === Status.Success && result) {
          // APIレスポンスから取得したポジションIDを使用
          const showPositions: PositionInfo[] = formData
            .filter((data) => !data.invalid && (data.atemQuestId || data.email))
            .map<PositionInfo>((formData) => {
              const apiPosition = positions?.find((p) => p.positionNumber === formData.positionNumber)
              return {
                positionNumber: formData.positionNumber,
                atemQuestId: formData.atemQuestId,
                positionId: apiPosition?.positionId ?? formData.positionId,
              }
            })
          navigate('/aubeQuestDealPurchaseForBankTransfer', { state: { contractPositions, showPositions } })
        } else if (status !== Status.Success) {
          setErrorMessage('AUBEクエストIDが重複しています。')
        }
      })
    }
    setIsSpinningSubmit(false)
  }

  /**
   * 「ポジション選択をやり直す」ボタン
   */
  const onCancel = (): void => {
    loginData.resetPurchaseData()
    navigate(prevScene)
  }

  React.useEffect(() => {
    positionsAPI((result) => {
      // 契約可能なPositionNumberのリスト
      const contractablePositionNumbers: PositionNumberType[] = []

      // 修正: maxPositionCountを固定で使用（重複position_numberの影響を受けない）
      const positionCount = result.maxPositionCount
      for (let i = 0; i < positionCount; i++) {
        const positionNumber = castConstantType<PositionNumberType>(i + 1, PositionNumber)
        if (!positionNumber) continue

        // 修正: 名義変更されていないポジション（新規登録）を優先して検索
        const position =
          result.positions.find((p) => p.positionNumber === positionNumber && !p.isChangedAssignment) ||
          result.positions.find((p) => p.positionNumber === positionNumber)
        // 契約可能判定: 入金済み+契約中のみが契約不可、それ以外は全て購入可能
        // - 未入金（解約含む）: 購入可能
        // - 入金済み+解約/CO: 購入可能（新規購入）
        // - 名義変更済み: 購入可能（元のユーザーから見ると枠が空いている）
        const isPurchasable = !position || position.isChangedAssignment || !(position.paymentStatus === PaymentStatus.Paid && position.cancellationType === Cancellation.Contract)
        if (isPurchasable) {
          // 契約可能なPositionNumber
          contractablePositionNumbers.push(positionNumber)
        }
      }

      // 契約を行うPositionNumberのリスト
      const targetPositionNumbers: PositionNumberType[] = []

      const positionContractData = loginData.getPositionContractSelectedData()
      if (!positionContractData) {
        navigate(prevScene)
        return
      } else if (positionContractData.selectedPositionNumber) {
        // ポジションが指定されていた場合
        if (contractablePositionNumbers.includes(positionContractData.selectedPositionNumber)) {
          // 契約可能
          targetPositionNumbers.push(positionContractData.selectedPositionNumber)
        } else {
          // 契約不可能なPositionNumber
          navigate(prevScene)
          return
        }
      } else {
        // ポジションが指定されていない場合
        if (positionContractData.additionPositionCount > contractablePositionNumbers.length) {
          // 契約可能数より多い
          navigate(prevScene)
          return
        } else {
          // 契約可能な全ポジションを表示（好きなポジションを選べるようにする）
          for (let i = 0; i < contractablePositionNumbers.length; i++) {
            targetPositionNumbers.push(contractablePositionNumbers[i])
          }
        }
      }

      // 契約可能なサブポジションの個数
      let contractableSubPositionCount = positionContractData.additionPositionCount
      if (contractablePositionNumbers.includes(PositionNumber.MainPosition)) {
        contractableSubPositionCount--
      }
      setSubPositionCount(contractableSubPositionCount)

      let count = 0
      const data: FormData[] = []
      const messages: FormMessages[] = []
      // 事前登録ユーザーかどうか
      const isRegistrationUser = loginData.getRegistrationStatus() === true
      // 契約を行うポジションのデータを作成
      targetPositionNumbers.forEach((positionNumber) => {
        // 修正: 名義変更されていないポジション（新規登録）を優先して検索
        const position =
          result.positions.find((p) => p.positionNumber === positionNumber && !p.isChangedAssignment) ||
          result.positions.find((p) => p.positionNumber === positionNumber)
        // 新規購入対象（解約/CO/名義変更）の場合はATEMクエストID・メールアドレスを空にする
        const isNewPurchase = position && (
          position.isChangedAssignment ||
          position.cancellationType === Cancellation.Unsubscribe ||
          position.cancellationType === Cancellation.CoolingOff
        )
        // 復活対象判定: 事前登録ユーザー + サブポジション + 未入金 + 解約
        // 復活対象の場合、バイナリマップは既に作成済みのため左右選択は不要
        const isRevivalTarget = isRegistrationUser &&
          positionNumber !== PositionNumber.MainPosition &&
          position?.paymentStatus === PaymentStatus.Unpaid &&
          position?.cancellationType === Cancellation.Unsubscribe
        const atemQuestId = isNewPurchase ? '' : (position?.atemQuestId ?? '')
        const positionId = position?.positionId ?? 0
        const email = isNewPurchase ? '' : (position?.atemQuestEmail ?? '')
        data.push({
          positionNumber,
          atemQuestId,
          positionId,
          email,
          invalid: false,
          binaryPlacement: null,
          isRevivalTarget,
        })
        messages.push({
          atemQuestId: [],
          email: [],
          binaryPlacement: [],
        })
        if (positionNumber !== PositionNumber.MainPosition) {
          count += !!atemQuestId || !!email ? 1 : 0
        }
      })
      if (count >= subPositionCount) {
        // すべてのサブポジションに入力されている
        for (const d of data) {
          if (d.positionNumber !== PositionNumber.MainPosition && !d.atemQuestId && !d.email) d.invalid = true
        }
      }
      setInputCount(count)
      setFormData(data)
      setFormMessages(messages)
    })
  }, [navigate, subPositionCount, prevScene])
  return (
    <>
      <Header title="ポジション契約" />
      <main>
        <section className="mx-4 my-5">
          <p className="note">
            AUBEクエストディール配布先のAUBEクエストIDをご入力下さい。
            <br />
            <span className="note-warning">ここで入力されたAUBEクエストID（ゲームのID）とコネクターIDが連結され報酬計算の対象となります。</span>
            <br />
            報酬取得権利確定課金の計算対象はこのポジション及び、このポジションから始まるパーソナルグループとなります。
            <br />
            サブポジションはポジション管理画面より、個別で決済する事も可能です。
            <br />
            <br />
            ＜サブポジションの配置ルール＞
            <br />
            サブポジションは「左」「右」いずれかの系列を必ず選択してください。選択した系列の最下層に配置されます。
            <br />
            <span className="note-warning">※配置は購入完了後に変更できません</span>
          </p>
          {formData.map(({ positionNumber, atemQuestId, email, invalid, binaryPlacement, isRevivalTarget }, index) => (
            <div key={index} className="mt-4">
              <div>
                <span className="font-15 fw-bold">{positionNumber === PositionNumber.MainPosition ? 'メインポジション' : `サブポジション${positionNumber - 1}`}</span>
                {positionNumber === PositionNumber.MainPosition && <span className="note ms-2">※必ず決済してください</span>}
              </div>
              <InputField
                name="atem-quest-id"
                type="text"
                value={atemQuestId}
                onChange={(e) => handleAtemQuestId(index, e.target.value)}
                placeholder="AUBEクエストID"
                aria-label="AUBEクエストID"
                disabled={invalid}
                errors={formMessages[index].atemQuestId}
                bold
              />
              <InputField
                name="atem-quest-email"
                type="email"
                value={email}
                onChange={(e) => handleEmail(index, e.target.value)}
                className="mt-2"
                placeholder="AUBEクエストに登録したメールアドレス"
                aria-label="AUBEクエストに登録したメールアドレス"
                disabled={invalid}
                errors={formMessages[index].email}
                bold
              />
              {/* サブポジションかつ復活対象でない場合のみ左右選択を表示 */}
              {/* 復活対象の場合、バイナリマップは既に作成済みのため左右選択は不要 */}
              {positionNumber !== PositionNumber.MainPosition && !isRevivalTarget && (
                <div className="mt-3">
                  <span className="fw-bold">配置選択</span>
                  <div className="d-flex gap-2 mt-2">
                    <input
                      type="radio"
                      className="btn-check"
                      id={`binaryPlacement-${index}-left`}
                      name={`binaryPlacement-${index}`}
                      checked={binaryPlacement === Binary.Left}
                      onChange={() => handleBinaryPlacement(index, Binary.Left)}
                      disabled={invalid}
                    />
                    <label className="btn btn-outline-primary flex-fill py-3" htmlFor={`binaryPlacement-${index}-left`}>
                      左
                    </label>
                    <input
                      type="radio"
                      className="btn-check"
                      id={`binaryPlacement-${index}-right`}
                      name={`binaryPlacement-${index}`}
                      checked={binaryPlacement === Binary.Right}
                      onChange={() => handleBinaryPlacement(index, Binary.Right)}
                      disabled={invalid}
                    />
                    <label className="btn btn-outline-primary flex-fill py-3" htmlFor={`binaryPlacement-${index}-right`}>
                      右
                    </label>
                  </div>
                  {formMessages[index].binaryPlacement.map((msg, msgIndex) => (
                    <div key={msgIndex} className="text-danger small mt-1">{msg}</div>
                  ))}
                </div>
              )}
              {/* 復活購入の場合は説明メッセージを表示 */}
              {positionNumber !== PositionNumber.MainPosition && isRevivalTarget && (
                <div className="mt-3">
                  <span className="note">※復活購入のため、配置位置は以前の設定が適用されます</span>
                </div>
              )}
            </div>
          ))}
          <div className="text-danger" hidden={errorMessage === ''}>
            {errorMessage}
          </div>
          <div className="mt-5">
            <Button variant="primary" className="w-100 py-4" disabled={isSpinningSubmit} onClick={onSubmit}>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" hidden={!isSpinningSubmit} />
              お申込みへ
            </Button>
          </div>
          <div className="mt-5 text-center">
            <Button variant="link" onClick={onCancel}>
              ポジション数選択をやり直す
            </Button>
          </div>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
